import { makeAuthenticatedRequest } from './authService';

export interface Chapter {
  chapter_id: string;
  title: string;
  order: number;
  block_id: string;
  content?: {
    block_id: string;
    content_type: string;
    text_value: string;
    created_at: string;
    updated_at: string;
  } | null;
}

export interface NovelCharacter {
  name: string;
  description: string;
  role: 'protagonist' | 'antagonist' | 'supporting' | 'other';
}

export interface NovelMetadata {
  oneParagraphSummary?: string;
  plots?: string[];
  characters?: NovelCharacter[];
  settings?: string[];
  themes?: string[];
  writingStyle?: string;
  targetAudience?: string;
  language?: string;
  notes?: string;
}

export interface Novel {
  id: number;
  novel_id: string;
  user_id: number;
  title: string;
  is_public: boolean;
  is_hidden: boolean;
  status?: 'on-going' | 'pending' | 'completed';
  genre?: string;
  metadata?: NovelMetadata;
  chapters: Chapter[];
  created_at: string;
  updated_at: string;
  author_name?: string; // Only present in public novels
}

export interface CreateNovelData {
  user_id: number;
  title: string;
  genre?: string;
  metadata?: NovelMetadata;
}

export interface UpdateNovelData {
  title: string;
  status?: 'on-going' | 'pending' | 'completed';
}

export interface CreateChapterData {
  title: string;
  user_id: number;
}

export interface UpdateChapterData {
  title?: string;
  content?: string;
}

export interface GetUserNovelsParams {
  limit?: number;
  sort_order?: 'ASC' | 'DESC';
}

// Helper function to build metadata context string
const buildMetadataContext = (
  genre?: string,
  metadata?: Partial<NovelMetadata>
): string => {
  if (!genre && (!metadata || Object.keys(metadata).length === 0)) {
    return '';
  }

  let context = 'Story Context:\n\n';

  if (genre) {
    context += `Genre: ${genre}\n\n`;
  }

  if (metadata?.oneParagraphSummary?.trim()) {
    context += `Summary: ${metadata.oneParagraphSummary}\n\n`;
  }

  if (metadata?.plots && metadata.plots.length > 0) {
    context += 'Plots:\n';
    metadata.plots.forEach(plot => {
      if (plot.trim()) {
        context += `- ${plot}\n`;
      }
    });
    context += '\n';
  }

  if (metadata?.characters && metadata.characters.length > 0) {
    context += 'Characters:\n';
    metadata.characters.forEach(char => {
      if (char.name.trim()) {
        context += `- ${char.name}`;
        if (char.role && char.role !== 'other') {
          context += ` (${char.role})`;
        }
        if (char.description.trim()) {
          context += `: ${char.description}`;
        }
        context += '\n';
      }
    });
    context += '\n';
  }

  if (metadata?.settings && metadata.settings.length > 0) {
    const settings = metadata.settings.filter(s => s.trim());
    if (settings.length > 0) {
      context += `Settings: ${settings.join(', ')}\n\n`;
    }
  }

  if (metadata?.themes && metadata.themes.length > 0) {
    const themes = metadata.themes.filter(t => t.trim());
    if (themes.length > 0) {
      context += `Themes: ${themes.join(', ')}\n\n`;
    }
  }

  if (metadata?.writingStyle?.trim()) {
    context += `Writing Style: ${metadata.writingStyle}\n\n`;
  }

  if (metadata?.targetAudience?.trim()) {
    context += `Target Audience: ${metadata.targetAudience}\n\n`;
  }

  if (metadata?.language?.trim()) {
    context += `Language: ${metadata.language}\n\n`;
  }

  if (metadata?.notes?.trim()) {
    context += `Additional Notes: ${metadata.notes}\n\n`;
  }

  context += '---\n\n';
  return context;
};

export const NovelService = {
  /**
   * Create a new novel
   */
  createNovel: async (data: CreateNovelData): Promise<Novel> => {
    const result = await makeAuthenticatedRequest('/novels', {
      method: 'POST',
      body: JSON.stringify(data),
    });

    return result.data;
  },

  /**
   * Get all novels for a user
   */
  getUserNovels: async (userId: number, params?: GetUserNovelsParams): Promise<Novel[]> => {
    const queryParams = new URLSearchParams();
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.sort_order) queryParams.append('sort_order', params.sort_order);

    const url = `/novels/user/${userId}${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    const result = await makeAuthenticatedRequest(url, {
      method: 'GET',
    });

    return result.data;
  },

  /**
   * Get a single novel by ID with chapter content
   */
  getNovel: async (novelId: string): Promise<Novel> => {
    const result = await makeAuthenticatedRequest(`/novels/${novelId}`, {
      method: 'GET',
    });

    return result.data;
  },

  /**
   * Update novel metadata (title)
   */
  updateNovel: async (novelId: string, data: UpdateNovelData): Promise<Novel> => {
    const result = await makeAuthenticatedRequest(`/novels/${novelId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });

    return result.data;
  },

  /**
   * Delete a novel and its chapters
   */
  deleteNovel: async (novelId: string): Promise<void> => {
    await makeAuthenticatedRequest(`/novels/${novelId}`, {
      method: 'DELETE',
    });
  },

  /**
   * Add a new chapter to a novel
   */
  createChapter: async (novelId: string, data: CreateChapterData): Promise<Chapter> => {
    const result = await makeAuthenticatedRequest(`/novels/${novelId}/chapters`, {
      method: 'POST',
      body: JSON.stringify(data),
    });

    return result.data;
  },

  /**
   * Get a single chapter by ID
   */
  getChapter: async (novelId: string, chapterId: string): Promise<Chapter> => {
    const result = await makeAuthenticatedRequest(
      `/novels/${novelId}/chapters/${chapterId}`,
      {
        method: 'GET',
      }
    );

    return result.data;
  },

  /**
   * Update chapter title or content
   */
  updateChapter: async (
    novelId: string,
    chapterId: string,
    data: UpdateChapterData
  ): Promise<Chapter> => {
    const result = await makeAuthenticatedRequest(
      `/novels/${novelId}/chapters/${chapterId}`,
      {
        method: 'PUT',
        body: JSON.stringify(data),
      }
    );

    return result.data;
  },

  /**
   * Delete a chapter
   */
  deleteChapter: async (novelId: string, chapterId: string): Promise<void> => {
    await makeAuthenticatedRequest(
      `/novels/${novelId}/chapters/${chapterId}`,
      {
        method: 'DELETE',
      }
    );
  },

  /**
   * Reorder chapters
   */
  reorderChapters: async (novelId: string, chapters: Chapter[]): Promise<Chapter[]> => {
    const result = await makeAuthenticatedRequest(
      `/novels/${novelId}/chapters/reorder`,
      {
        method: 'PUT',
        body: JSON.stringify({ chapters }),
      }
    );

    return result.data;
  },

  /**
   * Continue chapter using AI
   */
  continueChapter: async (
    provider: 'claude' | 'gemini' | 'openai' | 'grok',
    chapterContent: string,
    customPrompt?: string,
    previousChapters?: { title: string; content: string }[] | string[],
    chapterTitle?: string,
    maxTokens: number = 8000,
    genre?: string,
    selectedMetadata?: Partial<NovelMetadata>,
    allChapterNames?: string[]
  ): Promise<string> => {
    const hasContent = chapterContent && chapterContent.trim().length > 0;
    const hasPreviousChapters = previousChapters && previousChapters.length > 0;
    const isChapterNamesOnly = hasPreviousChapters && typeof previousChapters![0] === 'string';
    const hasAllChapterNames = allChapterNames && allChapterNames.length > 0;
    
    // Adapt system prompt based on context
    const systemPrompt = hasContent
      ? 'You are a creative writing assistant. Continue the following chapter naturally and engagingly. Write in a consistent style that matches the existing content.'
      : 'You are a creative writing assistant. Write a new chapter that flows naturally from the previous content and maintains consistent style and tone.';
    
    // Build user message with optional metadata context
    let userMessage = '';
    
    // Add metadata context first
    const metadataContext = buildMetadataContext(genre, selectedMetadata);
    if (metadataContext) {
      userMessage += metadataContext;
    }
    
    // Add all chapter names overview if provided
    if (hasAllChapterNames) {
      userMessage += 'Story structure (all chapter titles):\n';
      allChapterNames!.forEach((title, index) => {
        userMessage += `${index + 1}. ${title}\n`;
      });
      userMessage += '\n---\n\n';
    }
    
    // Add previous chapters context
    if (hasPreviousChapters) {
      if (isChapterNamesOnly) {
        userMessage += 'Previous chapter titles:\n';
        (previousChapters as string[]).forEach((title, index) => {
          userMessage += `${index + 1}. ${title}\n`;
        });
        userMessage += '\n';
      } else {
        userMessage += 'Here are the previous chapters for context:\n\n';
        (previousChapters as { title: string; content: string }[]).forEach((chapter) => {
          userMessage += `--- ${chapter.title} ---\n${chapter.content}\n\n`;
        });
        userMessage += '---\n\n';
      }
    }
    
    if (hasContent) {
      // Continue from existing content
      userMessage += `Please continue this chapter:\n\n${chapterContent}\n\n`;
    } else if (hasPreviousChapters) {
      // Start new chapter based on previous chapters
      if (chapterTitle) {
        userMessage += `Please write a new chapter titled "${chapterTitle}" that continues the story from the previous chapters.\n\n`;
      } else {
        userMessage += `Please write a new chapter that continues the story from the previous chapters.\n\n`;
      }
    } else {
      // Start completely fresh
      if (chapterTitle) {
        userMessage += `Please write a new chapter titled "${chapterTitle}". Start a new story.\n\n`;
      } else {
        userMessage += `Please write a new chapter. Start a new story.\n\n`;
      }
    }
    
    if (customPrompt && customPrompt.trim()) {
      userMessage += `Additional instructions: ${customPrompt.trim()}\n\n`;
    }
    
    if (hasContent) {
      userMessage += 'Continue writing naturally from where it left off.';
    } else {
      userMessage += 'Write naturally and engagingly.';
    }
    
    const messages = [
      {
        role: 'user',
        content: userMessage
      }
    ];

    const result = await makeAuthenticatedRequest('/ai/chat', {
      method: 'POST',
      body: JSON.stringify({
        provider,
        model: provider === 'claude' 
          ? 'claude-sonnet-4-5-20250929' 
          : provider === 'gemini' 
          ? 'gemini-2.5-flash' 
          : provider === 'grok'
          ? 'grok-4.6'
          : 'gpt-4o-mini',
        systemPrompt,
        messages,
        maxTokens,
        temperature: 0.7
      }),
    });

    // Backend returns: { success, data: { response, content, ... }, message }
    // The response object contains both 'content' (array of blocks) and 'response' (string)
    
    // First, try to get from result.data
    if (result && result.data) {
      // Try response field first (direct string)
      if (result.data.response && typeof result.data.response === 'string') {
        return result.data.response;
      }
      
      // Try content field (might be array of content blocks from Claude)
      if (result.data.content) {
        // If content is an array (Claude format), extract text from first block
        if (Array.isArray(result.data.content) && result.data.content.length > 0) {
          return result.data.content[0].text || '';
        }
        // If content is already a string
        if (typeof result.data.content === 'string') {
          return result.data.content;
        }
      }
    }
    
    // Fallback: try direct access
    if (result.response && typeof result.response === 'string') {
      return result.response;
    }
    
    if (result.content) {
      if (Array.isArray(result.content) && result.content.length > 0) {
        return result.content[0].text || '';
      }
      if (typeof result.content === 'string') {
        return result.content;
      }
    }
    
    return '';
  },

  /**
   * Rewrite entire chapter using AI
   */
  rewriteChapter: async (
    provider: 'claude' | 'gemini' | 'openai' | 'grok',
    chapterContent: string,
    customPrompt?: string,
    previousChapters?: { title: string; content: string }[] | string[],
    chapterTitle?: string,
    maxTokens: number = 8000,
    genre?: string,
    selectedMetadata?: Partial<NovelMetadata>,
    allChapterNames?: string[]
  ): Promise<string> => {
    const hasPreviousChapters = previousChapters && previousChapters.length > 0;
    const isChapterNamesOnly = hasPreviousChapters && typeof previousChapters![0] === 'string';
    const hasAllChapterNames = allChapterNames && allChapterNames.length > 0;
    
    const systemPrompt = 'You are a creative writing assistant. Rewrite the following chapter to improve its quality while maintaining the core story and style. Focus on enhancing clarity, flow, and engagement.';
    
    let userMessage = '';
    
    // Add metadata context first
    const metadataContext = buildMetadataContext(genre, selectedMetadata);
    if (metadataContext) {
      userMessage += metadataContext;
    }
    
    // Add all chapter names overview if provided
    if (hasAllChapterNames) {
      userMessage += 'Story structure (all chapter titles):\n';
      allChapterNames!.forEach((title, index) => {
        userMessage += `${index + 1}. ${title}\n`;
      });
      userMessage += '\n---\n\n';
    }
    
    // Add previous chapters context
    if (hasPreviousChapters) {
      if (isChapterNamesOnly) {
        userMessage += 'Previous chapter titles:\n';
        (previousChapters as string[]).forEach((title, index) => {
          userMessage += `${index + 1}. ${title}\n`;
        });
        userMessage += '\n';
      } else {
        userMessage += 'Here are the previous chapters for context:\n\n';
        (previousChapters as { title: string; content: string }[]).forEach((chapter) => {
          userMessage += `--- ${chapter.title} ---\n${chapter.content}\n\n`;
        });
        userMessage += '---\n\n';
      }
    }
    
    if (chapterTitle) {
      userMessage += `Please rewrite this chapter titled "${chapterTitle}":\n\n${chapterContent}\n\n`;
    } else {
      userMessage += `Please rewrite this chapter:\n\n${chapterContent}\n\n`;
    }
    
    if (customPrompt && customPrompt.trim()) {
      userMessage += `Additional instructions: ${customPrompt.trim()}\n\n`;
    }
    
    userMessage += 'Rewrite the entire chapter with improvements while keeping the same general structure and story.';
    
    const messages = [
      {
        role: 'user',
        content: userMessage
      }
    ];

    const result = await makeAuthenticatedRequest('/ai/chat', {
      method: 'POST',
      body: JSON.stringify({
        provider,
        model: provider === 'claude' 
          ? 'claude-sonnet-4-5-20250929' 
          : provider === 'gemini' 
          ? 'gemini-2.5-flash' 
          : provider === 'grok'
          ? 'grok-4.6'
          : 'gpt-4o-mini',
        systemPrompt,
        messages,
        maxTokens,
        temperature: 0.7
      }),
    });

    // Extract response (same logic as continueChapter)
    if (result && result.data) {
      if (result.data.response && typeof result.data.response === 'string') {
        return result.data.response;
      }
      
      if (result.data.content) {
        if (Array.isArray(result.data.content) && result.data.content.length > 0) {
          return result.data.content[0].text || '';
        }
        if (typeof result.data.content === 'string') {
          return result.data.content;
        }
      }
    }
    
    if (result.response && typeof result.response === 'string') {
      return result.response;
    }
    
    if (result.content) {
      if (Array.isArray(result.content) && result.content.length > 0) {
        return result.content[0].text || '';
      }
      if (typeof result.content === 'string') {
        return result.content;
      }
    }
    
    return '';
  },

  /**
   * Rewrite selected text using AI
   */
  rewriteSelection: async (
    provider: 'claude' | 'gemini' | 'openai' | 'grok',
    selectedText: string,
    customPrompt: string,
    contextBefore?: string,
    contextAfter?: string,
    maxTokens: number = 8000,
    genre?: string,
    selectedMetadata?: Partial<NovelMetadata>,
    allChapterNames?: string[]
  ): Promise<string> => {
    const systemPrompt = 'You are a creative writing assistant. Rewrite the selected text according to the user\'s instructions while maintaining consistency with the surrounding context.';
    
    let userMessage = '';
    
    // Add metadata context first
    const metadataContext = buildMetadataContext(genre, selectedMetadata);
    if (metadataContext) {
      userMessage += metadataContext;
    }
    
    // Add all chapter names overview if provided
    const hasAllChapterNames = allChapterNames && allChapterNames.length > 0;
    if (hasAllChapterNames) {
      userMessage += 'Story structure (all chapter titles):\n';
      allChapterNames!.forEach((title, index) => {
        userMessage += `${index + 1}. ${title}\n`;
      });
      userMessage += '\n---\n\n';
    }
    
    if (contextBefore || contextAfter) {
      userMessage += 'Here is the context:\n\n';
      if (contextBefore) {
        userMessage += `...${contextBefore}\n\n`;
      }
      userMessage += `[SELECTED TEXT TO REWRITE]\n${selectedText}\n[END SELECTED TEXT]\n\n`;
      if (contextAfter) {
        userMessage += `${contextAfter}...\n\n`;
      }
      userMessage += '---\n\n';
    } else {
      userMessage += `Selected text to rewrite:\n\n${selectedText}\n\n`;
    }
    
    userMessage += `Instructions: ${customPrompt}\n\n`;
    userMessage += 'Please rewrite only the selected text according to the instructions. Return only the rewritten text without any additional context or explanations.';
    
    const messages = [
      {
        role: 'user',
        content: userMessage
      }
    ];

    const result = await makeAuthenticatedRequest('/ai/chat', {
      method: 'POST',
      body: JSON.stringify({
        provider,
        model: provider === 'claude' 
          ? 'claude-sonnet-4-5-20250929' 
          : provider === 'gemini' 
          ? 'gemini-2.5-flash' 
          : provider === 'grok'
          ? 'grok-4.6'
          : 'gpt-4o-mini',
        systemPrompt,
        messages,
        maxTokens,
        temperature: 0.7
      }),
    });

    // Extract response (same logic as continueChapter)
    if (result && result.data) {
      if (result.data.response && typeof result.data.response === 'string') {
        return result.data.response;
      }
      
      if (result.data.content) {
        if (Array.isArray(result.data.content) && result.data.content.length > 0) {
          return result.data.content[0].text || '';
        }
        if (typeof result.data.content === 'string') {
          return result.data.content;
        }
      }
    }
    
    if (result.response && typeof result.response === 'string') {
      return result.response;
    }
    
    if (result.content) {
      if (Array.isArray(result.content) && result.content.length > 0) {
        return result.content[0].text || '';
      }
      if (typeof result.content === 'string') {
        return result.content;
      }
    }
    
    return '';
  },

  /**
   * Get all public novels
   */
  getPublicNovels: async (params?: { limit?: number; sort_order?: 'ASC' | 'DESC'; genre?: string }): Promise<Novel[]> => {
    const queryParams = new URLSearchParams();
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.sort_order) queryParams.append('sort_order', params.sort_order);
    if (params?.genre) queryParams.append('genre', params.genre);

    const url = `/novels/public${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    const result = await makeAuthenticatedRequest(url, {
      method: 'GET',
    });

    return result.data;
  },

  /**
   * Get a single public novel by ID
   */
  getPublicNovel: async (novelId: string): Promise<Novel> => {
    const result = await makeAuthenticatedRequest(`/novels/public/${novelId}`, {
      method: 'GET',
    });

    return result.data;
  },

  /**
   * Get a single public chapter by ID
   */
  getPublicChapter: async (novelId: string, chapterId: string): Promise<Chapter> => {
    const result = await makeAuthenticatedRequest(
      `/novels/public/${novelId}/chapters/${chapterId}`,
      {
        method: 'GET',
      }
    );

    return result.data;
  },

  /**
   * Update novel visibility (public/private)
   */
  updateNovelVisibility: async (novelId: string, isPublic: boolean): Promise<Novel> => {
    const result = await makeAuthenticatedRequest(`/novels/${novelId}/visibility`, {
      method: 'PUT',
      body: JSON.stringify({ is_public: isPublic }),
    });

    return result.data;
  },

  /**
   * Update novel hidden state
   */
  updateNovelHidden: async (novelId: string, isHidden: boolean): Promise<Novel> => {
    const result = await makeAuthenticatedRequest(`/novels/${novelId}/hidden`, {
      method: 'PUT',
      body: JSON.stringify({ is_hidden: isHidden }),
    });

    return result.data;
  },

  /**
   * Update novel metadata (genre and metadata fields)
   */
  updateNovelMetadata: async (novelId: string, genre?: string, metadata?: NovelMetadata): Promise<Novel> => {
    const result = await makeAuthenticatedRequest(`/novels/${novelId}/metadata`, {
      method: 'PUT',
      body: JSON.stringify({ genre, metadata }),
    });

    return result.data;
  },

  /**
   * Generate image using AI and upload to Firebase Storage
   */
  generateAndUploadImage: async (
    novelId: string,
    chapterId: string,
    prompt: string,
    selectedText?: string
  ): Promise<string> => {
    // Step 1: Generate image using backend API
    const result = await makeAuthenticatedRequest(
      `/novels/${novelId}/chapters/${chapterId}/generate-image`,
      {
        method: 'POST',
        body: JSON.stringify({ prompt, selectedText }),
      }
    );

    const { image, mimeType } = result.data;

    // Step 2: Convert base64 to blob
    const base64Response = await fetch(`data:${mimeType};base64,${image}`);
    const blob = await base64Response.blob();

    // Step 3: Upload to Firebase Storage
    const { storage } = await import('../firebase/config');
    const { ref, uploadBytes, getDownloadURL } = await import('firebase/storage');

    const timestamp = Date.now();
    const randomId = Math.random().toString(36).substring(7);
    const extension = mimeType.split('/')[1] || 'png';
    const fileName = `${timestamp}_${randomId}.${extension}`;
    const storagePath = `novels/${novelId}/images/${fileName}`;

    const storageRef = ref(storage, storagePath);
    await uploadBytes(storageRef, blob, { contentType: mimeType });

    // Step 4: Get public URL
    const downloadURL = await getDownloadURL(storageRef);
    return downloadURL;
  },

  /**
   * Generate metadata from chapters using AI
   */
  generateMetadataFromChapters: async (
    novelId: string,
    chapterIds: string[],
    fieldType: 'plots' | 'characters' | 'settings' | 'themes'
  ): Promise<Partial<NovelMetadata>> => {
    const result = await makeAuthenticatedRequest(
      `/novels/${novelId}/metadata/generate`,
      {
        method: 'POST',
        body: JSON.stringify({ chapterIds, fieldType }),
      }
    );

    return result.data;
  },

  /**
   * Enhance a metadata field using AI
   */
  enhanceMetadataField: async (
    novelId: string,
    fieldType: string,
    currentValue: any,
    instruction?: string
  ): Promise<any> => {
    const result = await makeAuthenticatedRequest(
      `/novels/${novelId}/metadata/enhance`,
      {
        method: 'POST',
        body: JSON.stringify({ fieldType, currentValue, instruction }),
      }
    );

    return result.data.enhanced;
  },
};

